import { useEffect, useState } from 'react';
import { Typography, Box, Button } from '@mui/material';
import { FormControlDateSelectFieldProps, FormElementControlPropsType } from './index';
import useBundleTranslation from 'i18n';
import { nanoid } from '@reduxjs/toolkit';
import ReactSelect from 'components/common/react-select/ReactSelect';
import styles from './FormDateTimeSelect.styles';
import IconMi from 'components/common/icon/IconMi';
import { FormComponentBuilder } from 'components/common/form/layout';

const getDayWordEnding = (day: number) => {
    let ending = 'th';
    const char = parseInt(day.toString().slice(-1));
    if (day != 11 && day != 12 && day != 13) {
        switch (char) {
            case 1:
                ending = 'st';
                break;
            case 2:
                ending = 'nd';
                break;
            case 3:
                ending = 'rd';
                break;
        }
    }
    return day + ending;
};

export default function FormDateSelect({ controlProps }: FormElementControlPropsType<FormControlDateSelectFieldProps>) {
    const { t } = useBundleTranslation(['app/distribution/notification_schedules']);

    const [value, setValue] = useState<string[]>(['1']);

    const dateRange = Array.from(Array(controlProps.fiscal ? 35 : 31).keys()).map((day) => ({
        label: getDayWordEnding(day + 1),
        value: `${day + 1}`,
        id: nanoid(),
    }));

    const addNewDate = () => {
        setValue([...value, dateRange[0].value]);
    };

    const removeDate = (indexToRemove: number) => {
        const copyValues = [...value];
        copyValues.splice(indexToRemove, 1);
        setValue(copyValues);
    };

    const updateDate = (indexToUpdate: number, newValue: string) => {
        const copyValues = [...value];
        copyValues.splice(indexToUpdate, 1, newValue);
        setValue(copyValues);
    };

    useEffect(() => {
        const formValue = controlProps.form.hookFormGetValues(controlProps.name) as String;

        if (formValue > '') {
            setValue(formValue.substring(1, formValue.length - 1).split(';;'));
        }
    }, []);

    useEffect(() => {
        controlProps.form.hookFormSetValue(controlProps.name, `;${value.join(';;')};`, { shouldDirty: true });
    }, [value]);

    return (
        <Box key={controlProps.name} sx={{ width: '149px' }}>
            <Typography variant="body1" gutterBottom component="div">
                {controlProps.label}
            </Typography>

            {Array.isArray(value) ? (
                <>
                    {value.map((item: string, index: number) => {
                        return (
                            <Box
                                sx={{
                                    ...styles.selectBox,
                                    ...styles.field,
                                    width: '100%',
                                    '&>.MuiBox-root': {
                                        width: '100%',
                                    },
                                }}
                                className="multiple"
                                key={index}
                            >
                                <ReactSelect
                                    data={dateRange}
                                    value={item}
                                    name={controlProps.name}
                                    update={(value: string) => updateDate(index, value)}
                                />
                                {value.length > 1 && (
                                    <IconMi onClick={() => removeDate(index)} icon={'times'} fontSize={'16'} />
                                )}
                            </Box>
                        );
                    })}
                </>
            ) : (
                <Box
                    sx={{
                        ...styles.selectBox,
                        width: '100%',
                        '&>.MuiBox-root': {
                            width: '100%',
                        },
                    }}
                >
                    <ReactSelect
                        data={dateRange}
                        value={value}
                        name={controlProps.name}
                        update={controlProps.onChange}
                        data-test={controlProps.uid}
                    />
                </Box>
            )}

            {controlProps.multiple && (
                <Button sx={{ mt: 1 }} onClick={addNewDate} startIcon={<IconMi icon={'new'} />} variant={'text'}>
                    {t('add_new_day')}
                </Button>
            )}
        </Box>
    );
}

export class FormDateSelectBuilder extends FormComponentBuilder {
    prepareProps(): FormControlDateSelectFieldProps {
        return {
            ...this.controlProps,
            multiple: this.elementProps.componentProps?.multiple ?? false,
            fiscal: this.elementProps.componentProps?.fiscal ?? false,
            selectedDates: this.elementProps.componentProps?.selectedDates ?? [],
        };
    }
}
